/**
 * Copyright 2016-2021 Sourcepole AG
 *           2019-2024 Stadt Wolfsburg
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */


import axios from 'axios';
import {SearchResultType} from 'qwc2/utils/SearchProviders';

/** ************************************************************************ **/
/**
 * WOB Koordinatensuche
 */

function wobCoordinatesSearch(text, searchParams, callback) {
    const displaycrs = searchParams.displaycrs || "EPSG:4326";
    const matches = text.match(/^\s*([+-]?\d+\.?\d*)[,\s]\s*([+-]?\d+\.?\d*)\s*$/);
    const items = [];
    if (matches && matches.length >= 3) {
        const x = parseFloat(matches[1]);
        const y = parseFloat(matches[2]);
        if (displaycrs !== "EPSG:4326" && displaycrs !== "EPSG:25832") {
            items.push({
                id: "coord0",
                text: x + ", " + y + " (" + displaycrs + ")",
                x: x,
                y: y,
                crs: displaycrs,
                bbox: [x, y, x, y]
            });
        // WOB: EPSG:25832 coordinates within Germany
        } else if (x >= 280371.0591000000131316 && x <= 921292.3712000000523403 && y >= 5235855.9768000002950430 && y <= 6106244.0058000003919005) {
            items.push({
                id: "coord" + items.length,
                text: x + ", " + y + " (EPSG:25832)",
                x: x,
                y: y,
                crs: "EPSG:25832",
                bbox: [x, y, x, y]
            });
        // WOB: EPSG:4326 (Lon, Lat) coordinates within Germany
        } else if (x >= 6.09803365 && x <= 15.57855525 && y >= 47.2394214 && y <= 54.9247172) {
            const title = Math.abs(x) + (x >= 0 ? "°E" : "°W") + ", "
                        + Math.abs(y) + (y >= 0 ? "°N" : "°S");
            items.push({
                id: "coord" + items.length,
                text: title,
                x: x,
                y: y,
                crs: "EPSG:4326",
                bbox: [x, y, x, y]
            });
        // WOB: EPSG:4326 (Lat, Lon) coordinates within Germany
        } else if (x >= 47.2394214 && x <= 54.9247172 && y >= 6.09803365 && y <= 15.57855525 && x !== y) {
            const title = Math.abs(x) + (x >= 0 ? "°N" : "°S") + ", "
                      + Math.abs(y) + (y >= 0 ? "°E" : "°W");
            items.push({
                id: "coord" + items.length,
                text: title,
                x: y,
                y: x,
                crs: "EPSG:4326",
                bbox: [y, x, y, x]
            });
        } else {
            if (x >= -180 && x <= 180 && y >= -90 && y <= 90) {
                const title = Math.abs(x) + (x >= 0 ? "°E" : "°W") + ", "
                          + Math.abs(y) + (y >= 0 ? "°N" : "°S");
                items.push({
                    id: "coord" + items.length,
                    text: title,
                    x: x,
                    y: y,
                    crs: "EPSG:4326",
                    bbox: [x, y, x, y]
                });
            }
            if (x >= -90 && x <= 90 && y >= -180 && y <= 180 && x !== y) {
                const title = Math.abs(y) + (y >= 0 ? "°E" : "°W") + ", "
                          + Math.abs(x) + (x >= 0 ? "°N" : "°S");
                items.push({
                    id: "coord" + items.length,
                    text: title,
                    x: y,
                    y: x,
                    crs: "EPSG:4326",
                    bbox: [y, x, y, x]
                });
            }
        }
    }
    const results = [];
    if (items.length > 0) {
        results.push(
            {
                id: "coords",
                titlemsgid: "search.coordinates",
                type: SearchResultType.PLACE,
                items: items
            }
        );
    }
    callback({results: results});
}

/** ************************************************************************ **/
/**
 * WOB Suche (Basisfunktionen)
 */

function wobParseSearchResults(obj, callback, topic, priority = 0) {
    const results = [];
    const groups = {};
    let groupcounter = 0;

    const provider = "wob_" + topic;

    (obj.results || []).map(entry => {
        if (!(entry.kategorie in groups)) {
            groups[entry.kategorie] = {
                id: provider + (groupcounter++),
                title: entry.kategorie,
                priority: priority,
                type: SearchResultType.PLACE,
                items: []
            };
            results.push(groups[entry.kategorie]);
        }

        groups[entry.kategorie].items.push({
            id: entry.id,
            text: entry.name,
            label: entry.name,
            x: 0.5 * (entry.bbox[0] + entry.bbox[2]),
            y: 0.5 * (entry.bbox[1] + entry.bbox[3]),
            crs: "EPSG:25832",
            bbox: entry.bbox,
            // geometry: entry.geojson,
            provider: provider
        });
    });
    callback({results: results});
}

function wobSearch(text, searchParams, callback, topic, priority = 0, limit = -1, simpleSearch = false) {
    let url;
    if (simpleSearch === true) {
        url = "https://geoportal.stadt.wolfsburg.de/wsgi/search_simple.wsgi";
    } else {
        url = "https://geoportal.stadt.wolfsburg.de/wsgi/search.wsgi";
    }
    const params = {
        query: text,
        topic: topic,
        limit: limit
    };
    axios.get(url, {params})
        .then(response => wobParseSearchResults(response.data, callback, topic, priority))
        .catch(() => wobParseSearchResults({results: {}}, callback, topic, priority));
}

function wobResultGeometry(resultItem, callback, topic) {
    const url = "https://geoportal.stadt.wolfsburg.de/wsgi/getResultGeometry.wsgi";
    const params = {
        id: resultItem.id,
        topic: topic
    };
    axios.get(url, {params}).then(response => {
        callback({geometry: response.data, crs: "EPSG:25832", hidemarker: false});
    });
}

/** ************************************************************************ **/
/**
 * WOB Suche: Infrastruktur
 */

function wobInfrastrukturSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "infrastruktur", 1);
}

function wobInfrastrukturResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "infrastruktur");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Stadt- und Ortsteile
 */

function wobStadtteileSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "stadtteile", 2);
}

function wobStadtteileResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "stadtteile");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Flurstücke
 */

function wobFlurstueckeSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "flurstuecke", 3);
}

function wobFlurstueckeResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "flurstuecke");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Flurstücke (WEB)
 */

function wobFlurstueckeWebSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "flurstuecke_web", 3);
}

function wobFlurstueckeWebResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "flurstuecke_web");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Adressen
 */

function wobAdressenSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "adressen", 4);
}

function wobAdressenResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "adressen");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Adressen (WEB)
 */

function wobAdressenWebSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "adressen_web", 4);
}

function wobAdressenWebResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "adressen_web");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Bebauungspläne
 */

function wobBebauungsplaeneSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "bebauungsplaene");
}

function wobBebauungsplaeneResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "bebauungsplaene");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Spielplätze
 */

function wobSpielplaetzeSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "spielplaetze");
}

function wobSpielplaetzeResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "spielplaetze");
}

/** ************************************************************************ **/
/**
 * WOB Suche: Besondere Standorte
 */

function wobBesondereStandorteSearch(text, searchParams, callback) {
    wobSearch(text, searchParams, callback, "besondere_standorte");
}

function wobBesondereStandorteResultGeometry(resultItem, callback) {
    wobResultGeometry(resultItem, callback, "besondere_standorte");
}

/** ************************************************************************ **/

window.QWC2SearchProviders = {
    coordinates: {
        labelmsgid: "search.coordinates",
        onSearch: wobCoordinatesSearch,
        handlesGeomFilter: false
    },
    wob_infrastruktur: {
        labelmsgid: "wob.search.infrastructure",
        onSearch: wobInfrastrukturSearch,
        getResultGeometry: wobInfrastrukturResultGeometry,
        handlesGeomFilter: false
    },
    wob_stadtteile: {
        labelmsgid: "wob.search.districts",
        onSearch: wobStadtteileSearch,
        getResultGeometry: wobStadtteileResultGeometry,
        handlesGeomFilter: false
    },
    wob_flurstuecke: {
        labelmsgid: "wob.search.flurstuecke",
        onSearch: wobFlurstueckeSearch,
        getResultGeometry: wobFlurstueckeResultGeometry,
        handlesGeomFilter: false
    },
    wob_flurstuecke_web: {
        labelmsgid: "wob.search.flurstuecke_web",
        onSearch: wobFlurstueckeWebSearch,
        getResultGeometry: wobFlurstueckeWebResultGeometry,
        handlesGeomFilter: false
    },
    wob_adressen: {
        labelmsgid: "wob.search.addresses",
        onSearch: wobAdressenSearch,
        getResultGeometry: wobAdressenResultGeometry,
        handlesGeomFilter: false
    },
    wob_adressen_web: {
        labelmsgid: "wob.search.addresses_web",
        onSearch: wobAdressenWebSearch,
        getResultGeometry: wobAdressenWebResultGeometry,
        handlesGeomFilter: false
    },
    wob_bebauungsplaene: {
        labelmsgid: "wob.search.bebauungsplaene",
        onSearch: wobBebauungsplaeneSearch,
        getResultGeometry: wobBebauungsplaeneResultGeometry,
        handlesGeomFilter: false
    },
    wob_spielplaetze: {
        labelmsgid: "wob.search.spielplaetze",
        onSearch: wobSpielplaetzeSearch,
        getResultGeometry: wobSpielplaetzeResultGeometry,
        handlesGeomFilter: false
    },
    wob_besondere_standorte: {
        labelmsgid: "wob.search.besondere_standorte",
        onSearch: wobBesondereStandorteSearch,
        getResultGeometry: wobBesondereStandorteResultGeometry,
        handlesGeomFilter: false
    }
};
